/**
 * Amount — the one money component (TMM-B1). It renders one number through the format module in
 * a `span` carrying the theme's `numeric` token (tabular numerals, TYPE-006 / TBL-010) and
 * `nowrap`. It formats only: it never converts, sums or rounds a value the server sent.
 *
 *   <Amount minor={1234} currency="USD" />             integer minor units
 *   <Amount minor={1234} currency="USD" whole />       whole units (tiles and charts)
 *   <Amount minor={-1234} currency="USD" signed />     "+" on a credit, a minus on a debit
 *   <Amount minor={-1234} currency="USD" accounting /> a negative in parentheses, in the error colour
 *   <Amount micro={1500} currency="USD" perMtok />     micro-units (per million tokens)
 *   <Amount count={9120} />                            a plain count
 *
 * A value the API didn't send (undefined or null) renders "—", never 0; a sent 0 renders 0.
 * `locale` defaults to the active language; `emphasis` sets weight 600 (TYPE-004). `signed` is
 * for lists that mix debits and credits (settlement and statement lines) and nowhere else.
 * `accounting` (TMMPDM-0089) is for the margin worksheet: costs are negative, in parentheses and red.
 */
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  formatMicro,
  formatMicroPerMtok,
  formatMoney,
  formatMoneyAccounting,
  formatMoneyWhole,
  formatNumber,
  signedOrDash,
  type Maybe,
} from '@/format';

interface CommonProps {
  locale?: string;
  emphasis?: boolean;
}

interface MinorProps extends CommonProps {
  minor: Maybe<number>;
  currency: string;
  whole?: boolean;
  signed?: boolean;
  accounting?: boolean;
  micro?: never;
  count?: never;
}

interface MicroProps extends CommonProps {
  micro: Maybe<number>;
  currency: string;
  perMtok?: boolean;
  minor?: never;
  count?: never;
}

interface CountProps extends CommonProps {
  count: Maybe<number>;
  minor?: never;
  micro?: never;
}

export type AmountProps = MinorProps | MicroProps | CountProps;

/** The key being present is the discriminator: `minor={undefined}` is still a money cell, one that was not sent. */
const isMinor = (props: AmountProps): props is MinorProps => 'minor' in props;
const isMicro = (props: AmountProps): props is MicroProps => 'micro' in props;

function amountText(props: AmountProps, locale: string): string {
  if (isMinor(props)) {
    if (props.signed) return signedOrDash(props.minor, props.currency, locale);
    if (props.accounting) return formatMoneyAccounting(props.minor, props.currency, locale);
    return props.whole
      ? formatMoneyWhole(props.minor, props.currency, locale)
      : formatMoney(props.minor, props.currency, locale);
  }
  if (isMicro(props)) {
    return props.perMtok
      ? formatMicroPerMtok(props.micro, props.currency, locale)
      : formatMicro(props.micro, props.currency, locale);
  }
  return formatNumber(props.count, locale);
}

export function Amount(props: AmountProps) {
  const { i18n } = useTranslation();
  const locale = props.locale ?? i18n.language;
  return (
    <Box
      component="span"
      sx={(theme) => ({
        ...theme.typography.numeric,
        whiteSpace: 'nowrap',
        ...(props.emphasis ? { fontWeight: 600 } : {}),
        ...(isMinor(props) && props.accounting && typeof props.minor === 'number' && props.minor < 0
          ? { color: theme.palette.error.main }
          : {}),
      })}
    >
      {amountText(props, locale)}
    </Box>
  );
}
